import React, { Suspense } from 'react';

import {
  fetchGetApplication,
  fetchGetStateByStateKey,
  fetchGetIwocapayPaymentLater,
} from '@iwoca/lapi-client/edge';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';

import { DecisionModal } from './components/DecisionModal/DecisionModal';
import { DecisionProvider } from './DecisionProvider';
import { SignupLayout } from './SignupLayout';
import { StepValidator } from './steps/StepValidator';
import { useGetPayLinkByPayLinkId } from '../../api/lending/lapiHooks';
import { LoadingScreen } from '../../components/LoadingScreen/LoadingScreen';
import { useSellerWhitelabelling } from '../../hooks/useSellerWhiteLabelling.hook';
import { useStateKey } from '../../hooks/useStateKey.hook';
import { useThreatMetrix } from '../../utils/ThreatMetrix';
import { LoadingCard } from '../components/LoadingCard/LoadingCard';
import { ModalProvider } from '../store/ModalProvider';
import { buildQueryString } from '../utils/queryParams';

export const Signup = ({ children }: { children: React.ReactNode }) => {
  const { stateKey } = useStateKey();
  const [, setSearchParams] = useSearchParams();

  const query = useQueries({
    queries: [
      {
        queryKey: ['fetchGetApplication'],
        queryFn: () =>
          fetchGetApplication({
            stateKey: stateKey!,
          }),
        enabled: Boolean(stateKey),
      },
      {
        queryKey: ['fetchGetState'],
        queryFn: () =>
          fetchGetStateByStateKey({
            stateKey: stateKey!,
          }),
        enabled: Boolean(stateKey),
      },
    ],
  });

  const { data, isLoading: isLoadingPaylink } = useQuery({
    queryKey: ['fetchGetIwocapayPaymentLater'],
    queryFn: async () => {
      const { data } = await fetchGetIwocapayPaymentLater({
        stateKey: stateKey!,
      });

      const payLinkId = data[0].id;
      const searchParams = buildQueryString({ payLinkId }, true);
      setSearchParams(searchParams);

      return { data };
    },
    enabled: Boolean(stateKey),
  });

  useThreatMetrix();

  const { payLink } = useGetPayLinkByPayLinkId({
    payLinkId: data?.data[0].id,
  });
  const { loadingWhitelabelling } = useSellerWhitelabelling(
    payLink?.seller_handle,
  );

  if (isLoadingPaylink || (!!payLink && loadingWhitelabelling))
    return <LoadingScreen />;

  const hasLoaded = query.every(
    ({ status, data }) => data && status === 'success',
  );

  return (
    <Suspense fallback={<LoadingScreen />}>
      <StepValidator>
        <ModalProvider>
          <DecisionProvider>
            <SignupLayout>
              {hasLoaded ? (
                <>{children}</>
              ) : (
                <div className="grid grid-cols-1 gap-xl">
                  <LoadingCard />
                  <LoadingCard />
                  <LoadingCard />
                </div>
              )}
            </SignupLayout>
            <DecisionModal />
          </DecisionProvider>
        </ModalProvider>
      </StepValidator>
    </Suspense>
  );
};
